<template>
  <ContentWrap title="AI参谋配置">
    <el-form :model="config" inline>
      <el-form-item label="功能状态">
        <el-switch
          v-hasPermi="['bosscircle:ai-advisor:enable']"
          v-model="enabled"
          :loading="statusSaving"
          @change="changeStatus"
        />
      </el-form-item>
      <el-form-item label="模型名称"><el-select v-model="config.modelId" clearable filterable class="!w-200px"><el-option v-for="item in modelOptions" :key="item.id" :label="item.name" :value="item.id" /></el-select></el-form-item>
      <el-form-item label="知识库名称"><el-select v-model="config.knowledgeId" clearable filterable class="!w-200px"><el-option v-for="item in knowledgeOptions" :key="item.id" :label="item.name" :value="item.id" /></el-select></el-form-item>
      <el-form-item label="每日额度"
        ><el-input-number v-model="config.dailyQuota" :min="0"
      /></el-form-item>
      <el-form-item label="允许付费"><el-switch v-model="config.paid" /></el-form-item>
      <el-form-item v-if="config.paid" label="商品名称">
        <BusinessLookupSelect v-model="config.productId" type="products" placeholder="请选择商品" />
      </el-form-item>
      <el-form-item>
        <el-button
          v-hasPermi="['bosscircle:ai-advisor:update']"
          type="primary"
          :loading="saving"
          @click="saveConfig"
        >
          保存配置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-form :model="query" inline>
      <el-form-item label="用户昵称"
        ><BusinessLookupSelect v-model="query.userId" type="users" placeholder="请选择用户"
      /></el-form-item>
      <el-form-item label="场景">
        <el-select v-model="query.sceneType" clearable class="!w-180px">
          <el-option v-for="item in sceneOptions" :key="item" :label="item" :value="item" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-140px">
          <el-option
            v-for="(label, value) in statusLabels"
            :key="value"
            :label="label"
            :value="Number(value)"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="调用时间">
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
        />
      </el-form-item>
    <el-form-item>
      <el-button type="primary" @click="search">查询</el-button>
    </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column label="用户昵称" min-width="120"><template #default="scope"><BusinessLookupName type="users" :id="scope.row.userId" /></template></el-table-column>
      <el-table-column prop="sceneType" label="场景" width="150" />
      <el-table-column label="状态" width="90">
        <template #default="scope">{{ statusLabels[scope.row.status] || '未知' }}</template>
      </el-table-column>
      <el-table-column label="Token" width="120">
        <template #default="scope"
          >{{ scope.row.inputTokens }}/{{ scope.row.outputTokens }}</template
        >
      </el-table-column>
      <el-table-column prop="durationMs" label="耗时(ms)" width="100" />
      <el-table-column prop="estimatedCost" label="预估成本" width="110" />
      <el-table-column prop="errorMessage" label="错误摘要" min-width="180" show-overflow-tooltip />
      <el-table-column prop="createTime" label="调用时间" width="180" />
      <el-table-column label="操作" width="80">
        <template #default="scope"
          ><el-button link type="primary" @click="showDetail(scope.row)">详情</el-button></template
        >
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="AI调用详情" width="760px">
    <el-descriptions v-if="detail" :column="2" border>
      <el-descriptions-item label="请求号">{{ detail.requestNo }}</el-descriptions-item>
      <el-descriptions-item label="链路ID">{{ detail.traceId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="业务类型">{{ detail.bizType || '-' }}</el-descriptions-item>
      <el-descriptions-item label="业务ID">{{ detail.bizId || '-' }}</el-descriptions-item>
    </el-descriptions>
    <el-form v-if="detail" label-position="top" class="mt-16px">
      <el-form-item label="脱敏输入摘要"
        ><el-input :model-value="detail.inputSummary" type="textarea" :rows="4" readonly
      /></el-form-item>
      <el-form-item label="结果数据"
        ><el-input :model-value="detail.resultData" type="textarea" :rows="6" readonly
      /></el-form-item>
      <el-form-item label="错误摘要"
        ><el-input :model-value="detail.errorMessage" type="textarea" :rows="2" readonly
      /></el-form-item>
    </el-form>
  </Dialog>
</template>

<script setup lang="ts">
import * as AiAdvisorApi from '@/api/business/aiAdvisor'
import { ModelApi } from '@/api/ai/model/model'
import { KnowledgeApi } from '@/api/ai/knowledge/knowledge'

defineOptions({ name: 'BossCircleAiAdvisor' })
const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const statusSaving = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const timeRange = ref<string[]>([])
const detailVisible = ref(false)
const detail = ref<any>()
const modelOptions = ref<any[]>([])
const knowledgeOptions = ref<any[]>([])
const sceneOptions = ['PEOPLE_ADVICE', 'OPENING', 'NEWS_DIGEST', 'CONTENT', 'MEETING']
const statusLabels: Record<number, string> = { 0: '处理中', 1: '成功', 2: '失败', 3: '降级' }
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined,
  sceneType: '',
  status: undefined
})
const config = reactive<AiAdvisorApi.AiAdvisorConfig>({
  dailyQuota: 0,
  paid: false,
  status: 0,
  version: 0
})
const enabled = computed({
  get: () => config.status === 1,
  set: (value) => (config.status = value ? 1 : 0)
})

const load = async () => {
  loading.value = true
  try {
    const data = await AiAdvisorApi.getAiAdvisorPage({
      ...query,
      beginTime: timeRange.value?.[0],
      endTime: timeRange.value?.[1]
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const loadConfig = async () => Object.assign(config, await AiAdvisorApi.getAiAdvisorConfig())
const search = () => {
  query.pageNo = 1
  load()
}
const saveConfig = async () => {
  saving.value = true
  try {
    const { status: _status, ...data } = config
    await AiAdvisorApi.saveAiAdvisorConfig(data)
    message.success('保存成功')
    await loadConfig()
  } finally {
    saving.value = false
  }
}
const changeStatus = async () => {
  statusSaving.value = true
  try {
    await AiAdvisorApi.updateAiAdvisorStatus(config.status, config.version)
    message.success('状态已更新')
  } finally {
    statusSaving.value = false
    await loadConfig()
  }
}
const showDetail = (row: any) => {
  detail.value = row
  detailVisible.value = true
}
onMounted(() => {
  load()
  loadConfig()
  ModelApi.getModelSimpleList().then((data) => { modelOptions.value = data || [] })
  KnowledgeApi.getSimpleKnowledgeList().then((data) => { knowledgeOptions.value = data || [] })
})
</script>
